import { isAllTimeSentinel } from "@utils/utils";
import ApexCharts from "apexcharts";
import { format } from "date-fns";
import React, { useEffect, useState } from "react";

import {
  Alert,
  CalendarDateRange,
  CalendarPicker,
  PredefinedLabel,
  Select,
  SelectItem,
} from "@fronterahealth/frontera-ui-components";

import {
  ApiReportGoalChartTypeChoices,
  ChartTypes,
  CumulativeTargets,
  GoalStatusEnums,
  ReportGoalType,
  useGetCumulativeTargetsQuery,
  useGetSessionTargetProgressQuery,
} from "@api/graphql/types-and-hooks";
import { ZeroLineRange } from "@components/Charts/CumulativeSocialInteractionChart";
import { ChartSeries, EventAnnotation, PhaseAnnotation } from "@components/Charts/SocialInteractionChart";
import { GoalChartView } from "@components/ShortTermGoalPanel/GoalChartView";
import { useMixedAxisDetection } from "@components/ShortTermGoalPanel/useMixedAxisDetection";
import { useRestrictToCumulative } from "@components/ShortTermGoalPanel/useRestrictToCumulative";
import { convertReadableString } from "@components/forms/utils";
import { DATE_FORMAT } from "@pages/VideoAnalytics/helpers";

const PRESET_LABELS: PredefinedLabel[] = [
  "All Time",
  "Last 1 Month",
  "Last 3 Months",
  "Last 6 Months",
  "Last 9 Months",
  "Last Year",
  "Custom",
];

interface ShortTermGoalProgressGraphProps {
  reportGoal: Pick<ReportGoalType, "id" | "summaryReviewed" | "summaryReviewedOn" | "chartFromDate" | "chartToDate">;
  selectedChart: SelectItem;
  setSelectedChart: (item: SelectItem) => void;
  signedChartImageUrl: string;
  forClientProgramming?: boolean;
  forReviewPage?: boolean;
  chartRef?: React.RefObject<ApexCharts | null>;
  filterSessionDates?: CalendarDateRange;
  setFilterSessionDates?: (dates: CalendarDateRange) => void;
  /** Fires once the chart settles its view mode; parents gate Approve on this to avoid persisting a stale chartType during the auto-switch race. */
  onSelectedChartReady?: (ready: boolean) => void;
}
export const chartLabelOverrides: Partial<Record<ApiReportGoalChartTypeChoices, string>> = {
  [ApiReportGoalChartTypeChoices.Progress]: "Default",
  [ApiReportGoalChartTypeChoices.Cumulative]: "Cumulative mastered targets",
};

const chartOrder: ApiReportGoalChartTypeChoices[] = [
  ApiReportGoalChartTypeChoices.Progress,
  ApiReportGoalChartTypeChoices.Cumulative,
  ApiReportGoalChartTypeChoices.AvgDaily,
  ApiReportGoalChartTypeChoices.AvgWeekly,
  ApiReportGoalChartTypeChoices.AvgMonthly,
  ApiReportGoalChartTypeChoices.AvgQuarterly,
  ApiReportGoalChartTypeChoices.TotalDaily,
  ApiReportGoalChartTypeChoices.TotalWeekly,
  ApiReportGoalChartTypeChoices.TotalMonthly,
  ApiReportGoalChartTypeChoices.TotalQuarterly,
];
const chartOptions: SelectItem[] = chartOrder.map((chartType) => ({
  id: chartType,
  primary: chartLabelOverrides[chartType] ?? convertReadableString(chartType),
}));

const PHASE_FILL_COLORS: Record<string, string> = {
  Baseline: "#00BFFF",
  Inactive: "#708090",
};
const DEFAULT_PHASE_FILL = "#90EE90";
const phaseFillColor = (status: string | null | undefined): string =>
  (status && PHASE_FILL_COLORS[status]) || DEFAULT_PHASE_FILL;

const truncateEventLabel = (text: string | null | undefined): string | null | undefined =>
  text && text.length > 30 ? `${text.substring(0, 30)}...` : text;

type ProgressEntry = { targetName?: string | null; data?: ({ date?: string; value?: number | null } | null)[] | null };
type GoalPhase = { status?: string | null; date?: string };
type GoalEvent = { text?: string | null; date?: string };

const buildSeries = (progress: (ProgressEntry | null)[] | null | undefined): ChartSeries[] =>
  (progress?.map((targetProgress) => ({
    name: targetProgress?.targetName as string,
    data: targetProgress?.data?.map((d) => ({ x: new Date(d?.date as string).getTime(), y: d?.value ?? null })),
  })) as ChartSeries[]) || [];

const buildPhaseAnnotations = (phases: (GoalPhase | null)[] | null | undefined): PhaseAnnotation[] =>
  (phases?.map((phase, index, arr) => {
    const nextPhase = arr[index + 1];
    return {
      label: phase?.status,
      position: new Date(phase?.date as string).getTime(),
      position2: nextPhase ? new Date(nextPhase.date as string).getTime() : undefined,
      area: true,
      fillColor: phaseFillColor(phase?.status),
    };
  }) as PhaseAnnotation[]) || [];

const buildEventAnnotations = (events: (GoalEvent | null)[] | null | undefined): EventAnnotation[] =>
  (events?.map((event) => ({
    label: truncateEventLabel(event?.text),
    position: new Date(event?.date as string).getTime(),
  })) as EventAnnotation[]) || [];

const earliestSessionTimestamp = (series: ChartSeries[]): number | undefined =>
  series
    .flatMap((s) => s.data ?? [])
    .map((point) => Number(point.x))
    .filter((x) => Number.isFinite(x))
    .reduce<number | undefined>((min, x) => (min === undefined || x < min ? x : min), undefined);

/**
 * Range of the Cumulative chart's flat line at 0 when nothing is mastered yet: the selected
 * dates, or for All Time the goal's first session through the cumulative query's end date.
 * Zero mastery is a real reassessment result, so report charts draw it as a line approval can
 * save. There is no range (the chart keeps "No data yet") for another chart type, a failed
 * cumulative query, the client Programming page, or a goal with no sessions (no data, not
 * zero mastery).
 */
const buildCumulativeZeroLineRange = ({
  isCumulative,
  cumulativeLoaded,
  forClientProgramming,
  startDate,
  endDate,
  series,
}: {
  isCumulative: boolean;
  cumulativeLoaded: boolean;
  forClientProgramming: boolean;
  startDate: Date | undefined;
  endDate: Date | string | undefined;
  series: ChartSeries[];
}): ZeroLineRange | undefined => {
  if (!isCumulative || !cumulativeLoaded || forClientProgramming) return undefined;
  const firstSession = earliestSessionTimestamp(series);
  if (firstSession === undefined) return undefined;
  const to = endDate ? new Date(endDate) : new Date();
  const from = startDate && !isAllTimeSentinel(startDate) ? new Date(startDate) : new Date(firstSession);
  return { from, to };
};

const graphTooltipText = (forClientProgramming: boolean, hasMixedMeasurementTypes: boolean): string | undefined => {
  if (forClientProgramming) return undefined;
  if (hasMixedMeasurementTypes)
    return "Only Cumulative mastered targets is available because this goal has targets with different measurement types.";
  return "Used in your report export. Choose the format that fits your data.";
};

/** Matches the Graph Type dropdown items so `id` is always set for API calls (e.g. approve/revoke). */
export const selectItemForGoalChartType = (chartType: ApiReportGoalChartTypeChoices): SelectItem => {
  const fromOptions = chartOptions.find((o) => o.id === chartType);
  if (fromOptions) return { ...fromOptions };
  return {
    id: chartType,
    primary: chartLabelOverrides[chartType] ?? convertReadableString(chartType),
  };
};

/** Apex `chart.id` for STG progress charts — must match approval capture in ReassessmentShortTermGoalPanel. */
const getShortTermGoalProgressChartId = (goalId: string, chartType: ApiReportGoalChartTypeChoices): string =>
  `stg_${goalId}_${convertReadableString(chartType)}`;

export const ShortTermGoalProgressGraph: React.FC<ShortTermGoalProgressGraphProps> = ({
  reportGoal,
  selectedChart,
  setSelectedChart,
  signedChartImageUrl,
  forClientProgramming = false,
  forReviewPage = false,
  chartRef,
  filterSessionDates,
  setFilterSessionDates,
  onSelectedChartReady,
}) => {
  const [calendarVersion, setCalendarVersion] = useState(0);

  const { data, isLoading } = useGetSessionTargetProgressQuery(
    {
      filter: {
        goalId: reportGoal.id,
        fromDate: filterSessionDates?.startDate
          ? format(new Date(filterSessionDates.startDate), DATE_FORMAT)
          : undefined,
        toDate: filterSessionDates?.endDate ? format(new Date(filterSessionDates.endDate), DATE_FORMAT) : undefined,
        excludeTargetStatus: [GoalStatusEnums.Inactive, GoalStatusEnums.Hold, GoalStatusEnums.Closed],
        chartType: selectedChart.id as ChartTypes,
      },
    },
    {
      enabled: !!reportGoal?.id,
      retry: false,
    },
  );

  const { axisGroupList, hasMixedMeasurementTypes, detectionReady } = useMixedAxisDetection(
    reportGoal.id,
    data?.getSessionProgress?.progress,
  );

  const cumulativeToDate = filterSessionDates?.endDate ?? reportGoal.summaryReviewedOn ?? undefined;
  // Pre-fire the cumulative query in parallel with session-progress. Costs one extra
  // request for non-mixed goals that never render Cumulative, but eliminates the
  // double-skeleton flash on mixed goals (PROA-1511 — chart auto-switches once detection
  // resolves and cumulative data is usually already there).
  const {
    data: cumulativeTargetsData,
    isLoading: isCumulativeLoading,
    isSuccess: isCumulativeSuccess,
  } = useGetCumulativeTargetsQuery(
    {
      filter: {
        goalId: reportGoal.id,
        targetStatus: [GoalStatusEnums.Mastered, GoalStatusEnums.Maintenance, GoalStatusEnums.Generalization],
        fromDate: filterSessionDates?.startDate
          ? format(new Date(filterSessionDates.startDate), DATE_FORMAT)
          : undefined,
        toDate: cumulativeToDate ? format(new Date(cumulativeToDate), DATE_FORMAT) : undefined,
      },
    },
    {
      enabled: !!reportGoal?.id,
      queryKey: [
        "get-cumulative-targets",
        reportGoal.id,
        reportGoal.summaryReviewed,
        selectedChart.id,
        filterSessionDates,
      ],
      retry: false,
    },
  );

  useEffect(() => {
    const restoredStart = reportGoal.chartFromDate ? new Date(reportGoal.chartFromDate) : undefined;
    setFilterSessionDates?.({
      startDate: restoredStart && !isAllTimeSentinel(restoredStart) ? restoredStart : undefined,
      endDate: reportGoal.chartToDate ? new Date(reportGoal.chartToDate) : undefined,
    });
    setCalendarVersion((v) => v + 1);
  }, [reportGoal.id]);

  const series = buildSeries(data?.getSessionProgress?.progress);
  const phaseAnnotations = buildPhaseAnnotations(data?.getSessionProgress?.goalPhases);
  const eventAnnotations = buildEventAnnotations(data?.getSessionProgress?.goalEvents);

  // Leave this undefined rather than "" when nothing is known. An empty string reads as a
  // real answer and hides the difference between "this goal has no declared measurement
  // type" and "we never asked". The backend now reports the target's declared type even
  // when the date filter returns no rows, so this only stays undefined for targets that
  // genuinely declare nothing.
  const progressType = data?.getSessionProgress?.progress?.find((p) => p?.progressType)?.progressType ?? undefined;

  const availableChartOptions = hasMixedMeasurementTypes
    ? chartOptions.filter((o) => o.id === ApiReportGoalChartTypeChoices.Cumulative)
    : chartOptions;

  // The chart we'll actually render. For mixed goals this is always Cumulative regardless of
  // whether the parent adopted the auto-switch (review pages pass a no-op setSelectedChart).
  const effectiveChartId = hasMixedMeasurementTypes
    ? ApiReportGoalChartTypeChoices.Cumulative
    : (selectedChart.id as ApiReportGoalChartTypeChoices);
  const effectiveChartIsCumulative = effectiveChartId === ApiReportGoalChartTypeChoices.Cumulative;

  useRestrictToCumulative({
    goalId: reportGoal.id,
    hasMixedMeasurementTypes,
    axisGroupList,
    selectedChart,
    setSelectedChart,
  });

  // Wait on detection before declaring readiness — otherwise Approve enables before we
  // know the goal is mixed, and a fast clicker could persist Progress on a goal that
  // should be Cumulative. Past detection, gate only on the mixed-type race + Cumulative
  // loading, which includes session progress because the zero line starts at the first
  // session (other charts stay ready and don't flicker on navigation).
  const autoSwitchPending = hasMixedMeasurementTypes && selectedChart.id !== ApiReportGoalChartTypeChoices.Cumulative;
  const cumulativeStillLoading = effectiveChartIsCumulative && (isCumulativeLoading || isLoading);
  const selectedChartReady = detectionReady && !autoSwitchPending && !cumulativeStillLoading;
  useEffect(() => {
    onSelectedChartReady?.(selectedChartReady);
  }, [selectedChartReady, onSelectedChartReady]);

  const chartId = getShortTermGoalProgressChartId(reportGoal.id, effectiveChartId);

  const zeroLineRange = buildCumulativeZeroLineRange({
    isCumulative: effectiveChartIsCumulative,
    cumulativeLoaded: isCumulativeSuccess,
    forClientProgramming,
    startDate: filterSessionDates?.startDate,
    endDate: cumulativeToDate,
    series,
  });

  const isAllTimeStartDate = filterSessionDates?.startDate && isAllTimeSentinel(filterSessionDates.startDate);
  const label =
    isAllTimeStartDate || !(filterSessionDates?.startDate && filterSessionDates?.endDate) ? "All Time" : undefined;

  return (
    <div className={`${!forClientProgramming ? "pt-6" : ""}`}>
      {!forReviewPage && (
        <div className="flex flex-row w-full justify-between items-center">
          <Select
            title="Graph Type"
            items={availableChartOptions}
            placeholderText="Graph Type"
            selected={selectedChart}
            setSelected={setSelectedChart}
            tooltipText={graphTooltipText(forClientProgramming, hasMixedMeasurementTypes)}
            className="w-full max-w-80! min-w-80!"
            disabled={!forClientProgramming && reportGoal?.summaryReviewed}
          />

          <CalendarPicker
            key={`${reportGoal.id}-${calendarVersion}`}
            placeholder="Select Date"
            selectedLabel={label}
            selectedDateRange={filterSessionDates}
            inputSize="md"
            onDateChange={(newDates) => setFilterSessionDates?.(newDates)}
            containerClass="max-w-[250px]! min-w-[250px]! ml-auto"
            variant="dropdown"
            presetLabels={PRESET_LABELS}
            showClearButton
            onClear={() => {
              setFilterSessionDates?.({});
            }}
            disabled={!forClientProgramming && reportGoal?.summaryReviewed}
          />
        </div>
      )}
      {hasMixedMeasurementTypes && !forReviewPage && (
        <Alert
          appearance="neutral"
          title="Mixed measurement types"
          text="Only Cumulative is available. Open a target chart for session-level values."
          className="mt-2"
        />
      )}
      {!forClientProgramming && reportGoal?.summaryReviewed && signedChartImageUrl ? (
        <div className="p-4 bg-white">
          <img src={signedChartImageUrl} alt="Invalid Chart Url" />
        </div>
      ) : (
        <GoalChartView
          detectionReady={detectionReady}
          effectiveChartIsCumulative={effectiveChartIsCumulative}
          chartId={chartId}
          cumulativeTargets={(cumulativeTargetsData?.getCumulativeTargets as CumulativeTargets[]) || []}
          cumulativeZeroLineRange={zeroLineRange}
          isCumulativeLoading={isCumulativeLoading}
          isLoading={isLoading}
          series={series}
          phaseAnnotations={phaseAnnotations}
          eventAnnotations={eventAnnotations}
          progressType={progressType}
          forClientProgramming={forClientProgramming}
          forReviewPage={forReviewPage}
          chartRef={chartRef}
          filterSessionDates={filterSessionDates}
        />
      )}
    </div>
  );
};
